import { privateRequest } from "../axios";
import type { ApiResponse, ExportFileResult, ListApiResponse } from "@/types/common";
import { parseFilenameFromContentDisposition } from "@/lib/downloadExcel";
import type {
  GetQuestionParams,
  PostQuestionRequest,
  PostQuestionResponse,
  QuestionField,
  QuestionOrderItem,
  UserType,
} from "@/types/question";

const ENDPOINTS = {
  QUESTIONS: "/questions",
};

const getAllQuestions = async (params?: GetQuestionParams) => {
  const response = await privateRequest.get<ListApiResponse<QuestionField>>(ENDPOINTS.QUESTIONS, { params });
  return response.data;
};

const postQuestion = async (data: PostQuestionRequest) => {
  const response = await privateRequest.post<ApiResponse<PostQuestionResponse>>(ENDPOINTS.QUESTIONS, data);
  return response.data;
};

const updateQuestion = async (id: string, data: PostQuestionRequest) => {
  const response = await privateRequest.patch<ApiResponse<PostQuestionResponse>>(`${ENDPOINTS.QUESTIONS}/${id}`, data);
  return response.data;
};

// Real endpoint given directly by the user: `PATCH /admin/questions` with
// the full new order in one request — every question's id + its new
// `displayOrder` — instead of one PATCH per question.
const updateQuestionsOrder = async (items: QuestionOrderItem[]) => {
  const response = await privateRequest.patch<ApiResponse<unknown>>(ENDPOINTS.QUESTIONS, items);
  return response.data;
};

const deleteQuestion = async (id: string) => {
  await privateRequest.delete(`${ENDPOINTS.QUESTIONS}/${id}`);
};

// Real endpoint/param, given directly by the user (same `isExport: true`
// convention confirmed for `GET /admin/action-logs` — see
// docs/decisions.md #74/#75). `userType` stays required (unlike the other
// pages' export filters, Founder/Investor isn't an optional narrowing of
// one shared list — it's which question set you're viewing, tied to the
// page's tab, not the "current filters vs. everything" row-scope choice);
// `search` is the only real optional filter, matching `GetQuestionParams`.
export interface ExportQuestionsParams {
  userType: UserType;
  search?: string | undefined;
}

const exportQuestions = async (params: ExportQuestionsParams): Promise<ExportFileResult> => {
  const response = await privateRequest.get(ENDPOINTS.QUESTIONS, {
    params: { ...params, isExport: true },
    responseType: "blob",
  });
  return {
    blob: response.data as Blob,
    fileName: parseFilenameFromContentDisposition(response.headers["content-disposition"]),
  };
};

const QuestionnaireService = {
  getAllQuestions,
  postQuestion,
  updateQuestion,
  updateQuestionsOrder,
  deleteQuestion,
  exportQuestions,
};

export default QuestionnaireService;
